/**
 * FrostedDehumidifierModal — Apple Home 風格毛玻璃 除濕機 控制面板
 *
 * 設計語言：
 * - 與 FrostedACModal 同系列 frosted-glass 設計
 * - 紫/violet tint → Dehumidifier 專用色調（除濕 = 乾燥 = 紫色）
 * - Humidity ring dial → 目標濕度 + 當前濕度
 * - Water tank status card → 水箱狀態
 * - Mode: Auto / Continuous / Laundry / Sleep
 * - Fan Speed: Low / Med / High / Auto
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './FrostedDehumidifierModal.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const DEHUMIDIFIER_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'dial', label: 'Humidity Dial', icon: 'humidity_percentage', slots: [
    { id: 'dial-arc', label: 'Arc Stroke', editableProps: ['color', 'glow'] },
    { id: 'dial-humidity', label: 'Humidity Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'dial-current', label: 'Current Humidity Text', editableProps: ['fontSize', 'color'] },
  ]},
  { id: 'mode', label: 'Mode', icon: 'auto_mode', slots: [
    { id: 'mode-bar', label: 'Mode Bar Container', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'mode-btn', label: 'Mode Button', editableProps: ['background', 'color', 'borderRadius', 'fontSize'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'fan', label: 'Fan Speed', icon: 'mode_fan', slots: [
    { id: 'fan-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'letterSpacing'] },
    { id: 'fan-btn', label: 'Speed Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'tank', label: 'Water Tank', icon: 'water_drop', slots: [] },
  { id: 'chart', label: 'Humidity History', icon: 'show_chart', slots: [] },
]

const DEFAULT_SECTION_ORDER = DEHUMIDIFIER_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODES = [
  { id: 'auto', label: 'Auto', icon: 'auto_mode' },
  { id: 'continuous', label: 'Continuous', icon: 'all_inclusive' },
  { id: 'laundry', label: 'Laundry', icon: 'local_laundry_service' },
  { id: 'sleep', label: 'Sleep', icon: 'dark_mode' },
] as const

const FAN_SPEEDS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Med' },
  { id: 'high', label: 'High' },
  { id: 'auto', label: 'Auto' },
] as const

const HUMIDITY_MIN = 30
const HUMIDITY_MAX = 80

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys)
  return v != null ? String(v) : ''
}

// Ring dial — 270° arc
const RING_R = 85
const RING_CX = 100
const RING_CY = 100
const RING_CIRC = 2 * Math.PI * RING_R
const RING_ARC_RATIO = 0.75
const RING_TRACK_OFFSET = RING_CIRC * (1 - RING_ARC_RATIO)

function humidityToDashOffset(humidity: number): number {
  const ratio = Math.max(0, Math.min(1, (humidity - HUMIDITY_MIN) / (HUMIDITY_MAX - HUMIDITY_MIN)))
  const arcLen = RING_CIRC * RING_ARC_RATIO
  return RING_CIRC - ratio * arcLen
}

const DEG = Math.PI / 180
function humidityToDotPos(humidity: number): { x: number; y: number } {
  const ratio = Math.max(0, Math.min(1, (humidity - HUMIDITY_MIN) / (HUMIDITY_MAX - HUMIDITY_MIN)))
  const angleDeg = 135 + ratio * 270
  const rad = angleDeg * DEG
  return {
    x: RING_CX + RING_R * Math.cos(rad),
    y: RING_CY + RING_R * Math.sin(rad),
  }
}

// Humidity comfort level
function humidityLevel(h: number): { label: string; color: string } {
  if (h < 30) return { label: '太乾燥', color: 'rgba(251,146,60,0.85)' }
  if (h <= 60) return { label: '舒適', color: 'rgba(74,222,128,0.85)' }
  return { label: '太潮濕', color: 'rgba(56,189,248,0.85)' }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FrostedDehumidifierModal({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  credentials,
}: DeviceModalTemplateProps) {

  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const isOn = Boolean(getAttr(deviceState, 'on'))
  const targetHumidity = numAttr(deviceState, 'humiditySetpointPercent', 'targetHumidity', 'humiditySetpoint') ?? 55
  const currentHumidity = numAttr(deviceState, 'humidityAmbientPercent', 'humidity', 'currentHumidity') ?? null
  const currentMode = strAttr(deviceState, 'currentModeSettings', 'mode') || 'auto'
  const currentFanSpeed = strAttr(deviceState, 'currentFanSpeedSetting', 'fanSpeed') || 'auto'
  const waterTankLevel = numAttr(deviceState, 'waterTankLevel', 'tankLevel') ?? null
  const waterTankFull = Boolean(getAttr(deviceState, 'waterTankFull', 'tankFull'))

  // ---- Settings ----
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label)
    setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ---- Rename ----
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])
  const startRename = useCallback(() => {
    setRenameValue(displayName)
    setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])
  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) { setIsRenaming(false); return }
    setDisplayName(trimmed)
    setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ---- Optimistic UI ----
  const [pendingHumidity, setPendingHumidity] = useState<number | null>(null)
  const [pendingMode, setPendingMode] = useState<string | null>(null)
  const [pendingFan, setPendingFan] = useState<string | null>(null)
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const displayHumidity = pendingHumidity ?? targetHumidity
  const displayMode = pendingMode ?? currentMode
  const displayFan = pendingFan ?? currentFanSpeed
  const displayPower = pendingPower ?? isOn
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => { if (pendingHumidity !== null && pendingHumidity === targetHumidity) setPendingHumidity(null) }, [targetHumidity, pendingHumidity])
  useEffect(() => { if (pendingMode !== null && pendingMode === currentMode) setPendingMode(null) }, [currentMode, pendingMode])
  useEffect(() => { if (pendingFan !== null && pendingFan === currentFanSpeed) setPendingFan(null) }, [currentFanSpeed, pendingFan])
  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])

  // ---- API ----
  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  const togglePower = useCallback(() => {
    if (isEditorMode) return
    const next = !displayPower
    setPendingPower(next)
    execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])

  const setHumidityCmd = useCallback((h: number) => {
    if (isEditorMode) return
    const clamped = Math.max(HUMIDITY_MIN, Math.min(HUMIDITY_MAX, h))
    setPendingHumidity(clamped)
    execCmd('setHumidity', { humidity: clamped })
  }, [isEditorMode, execCmd])

  const setModeCmd = useCallback((mode: string) => {
    if (isEditorMode) return
    setPendingMode(mode)
    execCmd('setMode', { mode })
  }, [isEditorMode, execCmd])

  const setFanSpeedCmd = useCallback((speed: string) => {
    if (isEditorMode) return
    setPendingFan(speed)
    execCmd('setFanSpeed', { fanSpeed: speed })
  }, [isEditorMode, execCmd])

  const holdHumidityRef = useRef(targetHumidity)
  useEffect(() => { holdHumidityRef.current = targetHumidity }, [targetHumidity])

  const startHold = useCallback((delta: number) => {
    holdTimerRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        const next = Math.max(HUMIDITY_MIN, Math.min(HUMIDITY_MAX, holdHumidityRef.current + delta))
        holdHumidityRef.current = next
        execCmd('setHumidity', { humidity: next })
      }, 200)
    }, 400)
  }, [execCmd])

  const stopHold = useCallback(() => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current)
    if (holdIntervalRef.current) clearInterval(holdIntervalRef.current)
    holdTimerRef.current = null
    holdIntervalRef.current = null
  }, [])

  // ---- Ring dial ----
  const valueDashOffset = humidityToDashOffset(displayHumidity)
  const dotPos = humidityToDotPos(displayHumidity)
  const comfortLevel = currentHumidity != null ? humidityLevel(currentHumidity) : null

  const stateClass = displayPower ? 'dh-frosted--on' : 'dh-frosted--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null

    switch (sectionId) {
      case 'dial':
        return (
          <div key="dial" className={`dh-frosted__dial${hl('dial')}`} data-modal-section="dial">
            <svg viewBox="0 0 200 200" className="dh-frosted__ring-svg">
              <defs>
                <linearGradient id="dhfVioletGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#a78bfa"/>
                  <stop offset="100%" stopColor="#c4b5fd"/>
                </linearGradient>
              </defs>
              <circle
                cx={RING_CX} cy={RING_CY} r={RING_R}
                fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={16}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={RING_TRACK_OFFSET}
                transform={`rotate(135,${RING_CX},${RING_CY})`}
              />
              <circle
                cx={RING_CX} cy={RING_CY} r={RING_R}
                fill="none" stroke="url(#dhfVioletGrad)" strokeWidth={16}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={displayPower ? valueDashOffset : RING_CIRC}
                transform={`rotate(135,${RING_CX},${RING_CY})`}
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
              {displayPower && (
                <circle cx={dotPos.x} cy={dotPos.y} r={7} fill="#fff"
                  style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}
                />
              )}
            </svg>
            <div className="dh-frosted__dial-text">
              {displayPower ? (
                <>
                  <div>
                    <span className="dh-frosted__humidity-value">{displayHumidity}</span>
                    <span className="dh-frosted__humidity-unit">%</span>
                  </div>
                  {currentHumidity != null && (
                    <div className="dh-frosted__current-humidity" style={{ color: comfortLevel?.color }}>
                      室內 {currentHumidity.toFixed(0)}% · {comfortLevel?.label}
                    </div>
                  )}
                </>
              ) : (
                <span className="dh-frosted__off-text">OFF</span>
              )}
            </div>
            {displayPower && !isEditorMode && (
              <>
                <button
                  style={{ position: 'absolute', left: 0, top: '30%', width: '35%', height: '50%', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  onClick={() => setHumidityCmd(displayHumidity - 5)}
                  onMouseDown={() => startHold(-5)}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={() => startHold(-5)}
                  onTouchEnd={stopHold}
                  aria-label="Decrease humidity"
                />
                <button
                  style={{ position: 'absolute', right: 0, top: '30%', width: '35%', height: '50%', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  onClick={() => setHumidityCmd(displayHumidity + 5)}
                  onMouseDown={() => startHold(5)}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={() => startHold(5)}
                  onTouchEnd={stopHold}
                  aria-label="Increase humidity"
                />
              </>
            )}
            <button className="dh-frosted__power-tap" onClick={togglePower}
              aria-label={displayPower ? 'Turn off' : 'Turn on'}
            />
          </div>
        )

      case 'mode':
        return (
          <div key="mode" className={`dh-frosted__modes${hl('mode')}`} data-modal-section="mode">
            {MODES.map(m => (
              <button key={m.id}
                className={`dh-frosted__mode-btn${displayMode === m.id ? ' active' : ''}`}
                onClick={() => setModeCmd(m.id)}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{m.icon}</span> {m.label}
              </button>
            ))}
          </div>
        )

      case 'fan':
        return (
          <div key="fan" className={`dh-frosted__fan${hl('fan')}`} data-modal-section="fan">
            <div className="dh-frosted__fan-label">Fan Speed</div>
            <div className="dh-frosted__fan-buttons">
              {FAN_SPEEDS.map(f => (
                <button key={f.id}
                  className={`dh-frosted__fan-btn${displayFan === f.id ? ' active' : ''}`}
                  onClick={() => setFanSpeedCmd(f.id)}
                >{f.label}</button>
              ))}
            </div>
          </div>
        )

      case 'tank':
        return (
          <div key="tank" className={`dh-frosted__tank-card${hl('tank')}`} data-modal-section="tank">
            <div className="dh-frosted__tank-header">
              <div className="dh-frosted__tank-title">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>water_drop</span>
                WATER TANK
              </div>
              <div className={`dh-frosted__tank-status${waterTankFull ? ' dh-frosted__tank-status--full' : ''}`}>
                {waterTankFull ? 'FULL' : waterTankLevel != null ? `${waterTankLevel}%` : 'OK'}
              </div>
            </div>
            {waterTankLevel != null && (
              <div className="dh-frosted__tank-bar">
                <div className="dh-frosted__tank-fill" style={{ width: `${Math.min(100, waterTankLevel)}%` }} />
              </div>
            )}
            <div className="dh-frosted__tank-hint">
              {waterTankFull ? 'Please empty the water tank' : 'Tank operating normally'}
            </div>
          </div>
        )

      case 'chart':
        return (
          <div key="chart" className={`dh-frosted__chart-section${hl('chart')}`} data-modal-section="chart">
            <div className="dh-frosted__chart-controls">
              <span className="dh-frosted__chart-ctrl">
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>schedule</span>
                24H
              </span>
              <span className="dh-frosted__chart-ctrl">
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>humidity_percentage</span>
                Humidity
              </span>
            </div>
            <div className="dh-frosted__chart-area">
              <svg viewBox="0 0 340 80" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                <defs>
                  <linearGradient id="dhChartFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(167,139,250,0.3)"/>
                    <stop offset="100%" stopColor="rgba(167,139,250,0.02)"/>
                  </linearGradient>
                </defs>
                <path d="M0,35 C20,38 40,42 60,45 C80,48 100,55 120,58 C140,55 160,48 180,42 C200,38 220,35 240,38 C260,42 280,48 300,45 C320,42 330,38 340,35" fill="none" stroke="rgba(167,139,250,0.7)" strokeWidth="2"/>
                <path d="M0,35 C20,38 40,42 60,45 C80,48 100,55 120,58 C140,55 160,48 180,42 C200,38 220,35 240,38 C260,42 280,48 300,45 C320,42 330,38 340,35 L340,80 L0,80 Z" fill="url(#dhChartFill)"/>
              </svg>
            </div>
            <div className="dh-frosted__chart-stats">
              <span>Now {currentHumidity ?? '--'}%</span>
              <span>Max --</span>
              <span>Min --</span>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className={`dh-frosted ${stateClass}`} data-modal-template-id="dehumidifier-frosted"
      onContextMenu={handleContextMenu}
    >
      {cssOverrides && <style>{cssOverrides}</style>}

      <div className="dh-frosted__header" data-modal-section="_modal">
        <button className="dh-frosted__back" onClick={onClose} aria-label="Back">‹</button>
        <span className="dh-frosted__name">{displayName}</span>
        <button
          className={`dh-frosted__power-btn${displayPower ? ' dh-frosted__power-btn--on' : ''}`}
          onClick={togglePower} aria-label="Power"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>power_settings_new</span>
        </button>
      </div>

      {orderedSections.map(id => renderSection(id))}

      <button className="dh-frosted__settings-btn" onClick={() => setShowSettings(true)} aria-label="Settings">
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>

      {showSettings && (
        <div className="dh-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="dh-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="dh-frosted__settings-header">
              <span className="dh-frosted__settings-title">設定</span>
              <button className="dh-frosted__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>
            <div className="dh-frosted__settings-section">
              <div className="dh-frosted__settings-section-title">裝置名稱</div>
              {isRenaming ? (
                <div className="dh-frosted__settings-row">
                  <input ref={renameInputRef} className="dh-frosted__rename-input"
                    value={renameValue} onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename} maxLength={40}
                  />
                </div>
              ) : (
                <div className="dh-frosted__settings-row">
                  <span className="dh-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>{displayName}</span>
                  <button className="dh-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>
            <div className="dh-frosted__settings-section">
              <div className="dh-frosted__settings-section-title">裝置資訊</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="dh-frosted__settings-row">
                  <span className="dh-frosted__settings-label">{item.label}</span>
                  <span className="dh-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button className="dh-frosted__settings-copy"
                      onClick={() => copyToClipboard(item.value, item.label)} title="Copy"
                    >📋{copiedField === item.label && <span className="dh-frosted__copied-toast">Copied</span>}</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
